/**
 * FAQ Item Component (FAQ = Frequently Asked Questions / Preguntas Frecuentes)
 * 
 * Individual FAQ (Frequently Asked Question) item with expandable/collapsible functionality.
 * Features:
 * - Clickable question button with dropdown arrow
 * - Smooth expand/collapse animation
 * - Answer text shown only when expanded
 * - Visual indicator (arrow rotation) for open/closed state
 * 
 * This component uses local state to manage its open/closed status,
 * allowing each FAQ item to be independently controlled.
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {string} props.question - The FAQ (Frequently Asked Question) question text
 * @param {string} props.answer - The FAQ answer text
 * @returns {JSX.Element} An expandable/collapsible FAQ (question/answer) item
 */

import React, { useId, useState } from 'react';
import './faq-item-style.css';

const FAQItem = ({ question, answer }) => {
  // State to track whether this FAQ (question/answer) item is expanded or collapsed
  const [isOpen, setIsOpen] = useState(false);
  const answerId = useId();

  /**
   * Toggles the open/closed state of the FAQ (question/answer) item
   * Called when user clicks on the question button
   */
  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={`faq-item ${isOpen ? 'faq-item--open' : ''}`}>
      {/* Clickable question button - toggles the answer visibility */}
      <button
        type="button"
        className="faq-item__question"
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-controls={answerId}
      >
        <span className="faq-item__question-text">{question}</span>
        {/* Dropdown arrow icon - rotates when item is open */}
        <div className={`faq-item__toggle ${isOpen ? 'faq-item__toggle--open' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>
      {/* Keep the answer mounted so height can animate smoothly without overlap */}
      <div
        id={answerId}
        className={`faq-item__answer-wrapper ${isOpen ? 'faq-item__answer-wrapper--open' : ''}`}
        aria-hidden={!isOpen}
      >
        <div className="faq-item__answer">
          <p>{answer}</p>
        </div>
      </div>
    </div>
  );
};

export default FAQItem;
